<template>
  <div class="email-composer">
    <!--
      What the message is built from, above the message: choosing a template
      rewrites the body, and linking a campaign adds a block to it. Both are
      decisions taken before writing, so they sit where the client reads them
      first rather than in a footer under the copy they would change.
    -->
    <div class="compose__setup">
      <label class="compose__tool">
        <span>Start from</span>
        <select class="compose__select" v-model="templateId" @change="applyTemplate">
          <option value="">Blank</option>
          <option v-for="template in templates" :key="template.id" :value="String(template.id)">
            {{ template.name }}
          </option>
        </select>
      </label>

      <div class="compose__tool">
        <span>Link a campaign</span>
        <campaign-picker
          :url="campaignsUrl"
          v-model="campaignId"
          name="campaign_id"
          placeholder="None"
          label="Link a campaign to this email"
        />
      </div>
    </div>

    <!--
      The header block of a compose window: two lines the client reads rather
      than fills in, then the two they write. `To` and `From` are text, not
      disabled inputs - a box you cannot type in invites a try; a line of text
      does not.
    -->
    <div class="compose__fields">
      <p class="compose__row">
        <span class="compose__key">To</span>
        <span class="compose__value">{{ audience }}</span>
      </p>
      <p class="compose__row">
        <span class="compose__key">From</span>
        <span class="compose__value">{{ sender }}</span>
        <span class="compose__note">Replies come back here</span>
      </p>

      <label class="visually-hidden" for="subject">Subject line</label>
      <input id="subject" class="compose__subject" type="text" name="subject" maxlength="200" required
             placeholder="Subject" :data-error="messages.subject" v-model="subject">

      <!--
        The field the form actually submits, and the field the browser
        validates. Trix is the control; this is what it writes into, so
        `required`, `maxlength` and the shared form validation keep working
        exactly as they did when this was the only thing here.

        maxlength mirrors the `max:65000` the request enforces, so a long paste
        is stopped here instead of bouncing off the server.
      -->
      <label class="visually-hidden" for="body_html">Email body</label>
      <textarea id="body_html" ref="body" class="visually-hidden" name="body_html" tabindex="-1"
                required maxlength="65000"
                :data-error="messages.bodyHtml" v-model="bodyHtml"></textarea>

      <!--
        The same editor the admin writes these email templates with in Nova.
        Every button on its toolbar produces markup the save keeps - see
        EmailCopySanitizer::ALLOWED_TAGS - so what the client leaves is what
        goes out.
      -->
      <trix-editor
        ref="editor"
        input="body_html"
        class="compose__body"
        placeholder="Write your email to the selected creators&hellip;"
        aria-label="Email body"
        @trix-change="onEditorInput"
        @trix-file-accept="refuseAttachment"
      ></trix-editor>
    </div>

    <!--
      Merge tags under the body, because that is what they act on: a click drops
      one in at the cursor rather than asking the client to copy it out of a
      hint. Buttons, not text, for the same reason.
    -->
    <div class="compose__tags">
      <button v-for="placeholder in placeholders" :key="placeholder" type="button"
              class="compose__tag" @click="insertPlaceholder(placeholder)">
        &#123;&#123; {{ placeholder }} &#125;&#125;
      </button>
    </div>

    <p class="marketing-hint">
      Basic HTML is allowed, and a merge tag is replaced with that creator&rsquo;s own details as it sends.
      Choosing a template rewrites the body &mdash; the shared template itself is never changed.
    </p>

    <!--
      Two sentences rather than one, because the old copy described what
      linking a campaign does and then stayed put when the client chose not to
      - explaining the other option at exactly the moment they needed this one.
      The consequence that is easy to miss is stated outright: the button is the
      only link in the email, so without it there is no click to measure and the
      report can only ever show opens.
    -->
    <p class="marketing-hint">
      <template v-if="campaignId">
        A linked campaign adds a &ldquo;View Challenge&rdquo; button. The report then follows everyone who clicks it
        &mdash; through joins and submissions, all the way to rewards earned.
      </template>
      <template v-else>
        With no campaign linked the email goes out as copy alone. That button is its only link, so there is nothing
        to click and the report will show opens and nothing after them.
      </template>
    </p>

    <div class="email-composer__preview">
      <div class="section-title">
        <h1>Preview</h1>
        <p>
          Exactly what a creator sees, updating as you type.
          <span class="email-composer__state">{{ state }}</span>
        </p>
      </div>
      <!--
        allow-same-origin WITHOUT allow-scripts. Scripts inside the frame cannot
        run at all, so the origin grant is not something injected markup could
        use; what it buys is the parent being able to write into the document,
        which is how typing updates the preview instantly instead of reloading
        it. (allow-scripts together with allow-same-origin would be the unsafe
        pair - it is deliberately absent.)
      -->
      <iframe ref="frame" class="marketing-preview" sandbox="allow-same-origin" title="Email preview"></iframe>
    </div>
  </div>
</template>

<script>
/**
 * The email step of the campaign builder: the copy and the preview of it.
 *
 * They are one component because they are one thing. Keeping the preview
 * separate meant reaching across the page for the fields it had to watch, and
 * meant a template picker that wrote to a textarea in code had to announce
 * itself by hand - a listener plus a zero-delay timeout to work around the
 * input event that never fires. Here the copy is state, and the preview is
 * something that follows it.
 *
 * The inputs keep their name attributes and stay inside the Blade form, so the
 * step still submits and validates exactly as before; this component owns how
 * they behave, not where they go.
 *
 * Rendering stays on the server. The preview has to be the real email - the
 * same layout, the same placeholder substitution, the same sanitizer the save
 * applies - and a second implementation here would drift, showing a preview of
 * an email nobody receives.
 */
import 'trix'

import CampaignPicker from './CampaignPicker.vue'

export default {
  name: 'EmailComposer',

  components: { CampaignPicker },

  props: {
    previewUrl: {
      type: String,
      required: true
    },
    // Authored in EmailCampaignRequest::FIELD_MESSAGES and passed down, so the
    // browser shows the sentence the server would have answered with.
    messages: {
      type: Object,
      required: true
    },
    // [{ id, name, content }] - lifecycle copy to start from.
    templates: {
      type: Array,
      required: false,
      default: () => []
    },
    // Endpoint listing the client's own challenges, a page at a time.
    campaignsUrl: {
      type: String,
      required: true
    },
    placeholders: {
      type: Array,
      required: false,
      default: () => []
    },
    // Read-only lines in the compose header: who receives this, and the name
    // and address it arrives under. Both are decided elsewhere - the audience
    // step and the platform's mail settings - so they are shown, not offered.
    audience: {
      type: String,
      required: false,
      default: ''
    },
    sender: {
      type: String,
      required: false,
      default: ''
    },
    initial: {
      type: Object,
      required: false,
      default: () => ({})
    },
    // { first_name: 'Keyshawn', brand_name: ..., ... } as the server resolves
    // them, so the same placeholders read the same in both renders.
    tokens: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },

  data() {
    return {
      subject: this.initial.subject || '',
      bodyHtml: this.initial.bodyHtml || '',
      campaignId: this.initial.campaignId ? String(this.initial.campaignId) : '',
      templateId: '',
      applying: false,
      state: '',
      ready: false,
      timer: null,
      inFlight: null
    }
  },

  watch: {
    subject: 'onEdit',

    bodyHtml() {
      // The editor is the only thing the client touches, so the field the form
      // submits is always written to in code.
      this.announceWrite()

      // A write this component made rather than one the editor reported: the
      // copy has to be put into the editor as well.
      if (this.applying) {
        this.applying = false
        this.loadIntoEditor()
      }

      this.onEdit()
    },

    // A linked challenge adds or removes the whole call-to-action block, which
    // is a structural change rather than a text one, so it goes straight to the
    // server instead of being patched locally.
    campaignId: 'reconcile'
  },

  mounted() {
    this.bootstrap()
  },

  methods: {
    /**
     * Tell the DOM that the body changed.
     *
     * Vue writes the new value straight onto the textarea, and assigning a
     * value fires no input event. Anything outside the component that follows
     * the field - the form validation in client/marketing.js - therefore never
     * learns the body is no longer empty, and leaves its error standing under a
     * field that now has copy in it.
     *
     * Every change needs this now, not just the ones this component makes:
     * nobody types into that textarea any more. The client types into Trix, and
     * the field they are really filling in never sees a keystroke.
     */
    announceWrite() {
      this.$nextTick(() => {
        const field = this.$refs.body

        if (field) {
          field.dispatchEvent(new Event('input', { bubbles: true }))
        }
      })
    },

    applyTemplate() {
      // "Write a new one" is the empty option, and it is a choice like any
      // other: start from nothing. Without this it fell through the guard
      // below - find() cannot match an empty id - and left the previous
      // template's copy sitting in an editor whose picker said the client was
      // writing their own.
      const template = this.templateId
        ? this.templates.find((candidate) => String(candidate.id) === this.templateId)
        : { content: '' }

      if (!template || template.content === this.bodyHtml) return

      // Flags the write as this component's own, so the watcher announces it to
      // the DOM instead of reading it as the client typing.
      this.applying = true
      this.bodyHtml = template.content
    },

    /**
     * Drop a merge tag in at the cursor, replacing whatever is selected - the
     * same thing typing it there would have done.
     *
     * Handed to the editor rather than spliced into the string: Trix owns the
     * document and the caret, and a string written underneath it would be
     * overwritten by its next keystroke.
     *
     * @param {string} name
     */
    insertPlaceholder(name) {
      const editor = this.$refs.editor?.editor

      if (!editor) {
        return
      }

      editor.insertString('{{ ' + name + ' }}')
      this.$refs.editor.focus()
    },

    /**
     * The editor changed the document; its HTML is the truth from here.
     *
     * Guarded on equality because loading copy into the editor also raises
     * this - without it, a template applied would bounce straight back.
     */
    onEditorInput() {
      const html = this.$refs.editor?.innerHTML ?? ''

      if (html === this.bodyHtml) {
        return
      }

      this.bodyHtml = html
    },

    /**
     * Push the copy this component holds into the editor.
     *
     * Only for writes the editor did not make - a template the client chose.
     * The saved draft is not one of them: `input="body_html"` points Trix at
     * the field the server already rendered the copy into, and Trix hydrates
     * itself from it.
     *
     * That attribute is not optional. Without it Trix makes an input of its
     * own, empty, and loads that over the document during initialisation - so
     * copy pushed in by hand was wiped a tick later and a saved draft opened
     * into an empty editor.
     */
    loadIntoEditor() {
      const el = this.$refs.editor

      if (el?.editor && el.innerHTML !== this.bodyHtml) {
        el.editor.loadHTML(this.bodyHtml || '')
      }
    },

    /**
     * Trix can attach files; this email cannot carry them.
     *
     * Refused at the editor rather than dropped at the save, so a client never
     * sees an image sit in their copy and then vanish from what goes out. The
     * sanitizer drops one too, but by then the promise has already been made.
     *
     * @param {Event} event
     */
    refuseAttachment(event) {
      event.preventDefault()
    },

    /**
     * Every edit is painted straight into the preview, and only then reconciled
     * with the server once typing pauses.
     *
     * The paint is what makes it feel immediate: no request, no reload, no
     * flash, and the scroll position stays where the client left it. The
     * reconcile is what keeps it honest - it is the server's render, with the
     * real sanitizer applied, and in the ordinary case it lands on markup
     * identical to what is already on screen, so nothing visibly happens.
     */
    onEdit() {
      this.paint()
      clearTimeout(this.timer)
      this.timer = setTimeout(this.reconcile, 700)
    },

    /**
     * @return {Document|null}
     */
    doc() {
      return this.ready ? (this.$refs.frame?.contentDocument ?? null) : null
    },

    /**
     * Resolve {{ tokens }} the way ClientBlastEmail::personalize() does:
     * substitution only, values escaped, unknown tokens left visible so a typo
     * shows up here instead of silently blanking a sentence.
     * @param {string} copy
     * @return {string}
     */
    resolve(copy) {
      return String(copy).replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (match, name) => {
        const value = this.tokens[String(name).toLowerCase()]

        return value === undefined
          ? match
          : String(value).replace(/[&<>"']/g, (c) => ({
              '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
            }[c]))
      })
    },

    paint() {
      const doc = this.doc()

      if (!doc) return

      const subject = doc.getElementById('cr-preview-subject')
      const body = doc.getElementById('cr-preview-body')

      if (subject) subject.textContent = this.resolve(this.subject)
      if (body) body.innerHTML = this.resolve(this.bodyHtml)
    },

    async bootstrap() {
      const html = await this.fetch()

      if (html === null) return

      const frame = this.$refs.frame

      frame.addEventListener('load', () => {
        this.ready = true
        this.paint()
      }, { once: true })

      // The only time the document is replaced wholesale.
      frame.srcdoc = html
    },

    /**
     * Bring the preview in line with what the server would actually send.
     *
     * Patches only the three regions that can change while the client edits.
     * Replacing the whole body instead - which is what this used to do - throws
     * away and rebuilds every node including the header logo, and the browser
     * re-decoding that image is the flash that appeared a beat after typing
     * stopped. Nothing outside these anchors depends on the copy, so there is
     * nothing else to update.
     */
    async reconcile() {
      // Remember what this render is based on, so a response that lands after
      // further typing can be recognised as stale.
      const basis = { subject: this.subject, bodyHtml: this.bodyHtml }
      const html = await this.fetch()
      const doc = this.doc()

      if (html === null || !doc) return

      const parsed = new DOMParser().parseFromString(html, 'text/html')

      const patched = [
        this.swap(doc, parsed, 'cr-preview-subject', 'innerHTML'),
        this.swap(doc, parsed, 'cr-preview-body', 'innerHTML'),
        // outerHTML: the call-to-action row is replaced whole, which is how it
        // can appear and disappear as the linked challenge changes.
        this.swap(doc, parsed, 'cr-preview-cta', 'outerHTML')
      ].every(Boolean)

      if (!patched) {
        // An anchor is missing - an older render, or a template without them.
        // Rebuilding is worse than a flash only if nothing updates at all.
        doc.body.innerHTML = parsed.body.innerHTML
      }

      // Only when the client kept typing while that was in the air. Repainting
      // unconditionally would put the raw copy straight back over the render
      // that just went through the sanitizer.
      if (basis.subject !== this.subject || basis.bodyHtml !== this.bodyHtml) {
        this.paint()
      }
    },

    /**
     * Copy one region across, and only if it actually differs - in the ordinary
     * case the server agrees with what is already on screen and nothing is
     * written at all.
     *
     * @param {Document} doc
     * @param {Document} parsed
     * @param {string} id
     * @param {'innerHTML'|'outerHTML'} property
     * @return {boolean} Whether the region was found in both documents.
     */
    swap(doc, parsed, id, property) {
      const target = doc.getElementById(id)
      const source = parsed.getElementById(id)

      if (!target || !source) return false

      if (target[property] !== source[property]) {
        target[property] = source[property]
      }

      return true
    },

    /**
     * @return {Promise<string|null>}
     */
    async fetch() {
      // Only the newest edit matters; abandon whatever is still in the air so a
      // slow response cannot land on top of a newer one.
      if (this.inFlight) this.inFlight.abort()
      this.inFlight = new AbortController()

      this.state = 'Syncing…'

      try {
        const response = await window.fetch(this.previewUrl, {
          method: 'POST',
          signal: this.inFlight.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || ''
          },
          body: JSON.stringify({
            subject: this.subject,
            body_html: this.bodyHtml,
            campaign_id: this.campaignId
          })
        })

        if (!response.ok) {
          this.state = 'Preview may be out of date.'
          return null
        }

        this.state = ''

        return await response.text()
      } catch (e) {
        // Aborting is the expected outcome of typing, not a failure.
        if (e.name !== 'AbortError') this.state = 'Preview may be out of date.'

        return null
      }
    }
  }
}
</script>
